/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllRoutes } from '../modules/routes/stores/slice';
import { DATE_BACKEND_FORMAT, PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../constants';
import { doGetRoutes, doDeleteRoute } from '../modules/routes/stores/thunk';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import RoutesTable from '../components/Tables/RoutesTable';
import { Button, Input, Popconfirm, Drawer, DatePicker, Select, Space, Badge, message } from 'antd';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;

const RoutesPage = () => {
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const ROUTE_STATUSES = Object.keys(t('routeStatuses', { returnObjects: true })).map((key) => ({
    value: key,
    label: t(`routeStatuses.${key}`),
  }));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const routes = useSelector(selectAllRoutes);

  const { metadata, isFetching } = useSelector((state) => state.route);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const tableConfigRef = useRef({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [filterDriver, setFilterDriver] = useState('');
  const [filterStatus, setFilterStatus] = useState(undefined);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  const [configPageSize, setConfigPageSize] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ovo-table-config-routes-columns') || '{}').pageSize || PAGE_SIZE;
    } catch {
      return PAGE_SIZE;
    }
  });
  const itemsPerPage = query.pageSize || configPageSize;
  const pagination = {
    current: currentPage || 1,
    pageSize: Number(itemsPerPage),
    locale: { items_per_page: tc('itemsPerPage', { entity: t('title') }) },
    total: metadata.totalItems || routes.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  useEffect(() => {
    if (query.q) {
      setSearchValue(query.q);
    }
  }, []);

  useEffect(() => {
    dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.locale;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = 1;
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetRoutes({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleTableChange = (paginationParams, _filters, sorter) => {
    const newParams = {
      ...query,
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
    };

    // Map Ant Design sorter to backend params
    if (sorter && sorter.field && sorter.order) {
      newParams.sortField = sorter.field;
      newParams.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      // Sort cleared — remove sort params
      delete newParams.sortField;
      delete newParams.sortOrder;
    }

    handleFilter(newParams, true);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleApplyFilters = () => {
    const filterParams = { ...query };
    if (filterDateRange && filterDateRange[0] && filterDateRange[1]) {
      filterParams.dateFrom = filterDateRange[0].format('YYYY-MM-DD');
      filterParams.dateTo = filterDateRange[1].format('YYYY-MM-DD');
    } else {
      delete filterParams.dateFrom;
      delete filterParams.dateTo;
    }
    if (filterDriver) {
      filterParams.driver = filterDriver;
    } else {
      delete filterParams.driver;
    }
    if (filterStatus) {
      filterParams.status = filterStatus;
    } else {
      delete filterParams.status;
    }
    filterParams.current = 1;
    handleFilter(filterParams, true);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilterDateRange(null);
    setFilterDriver('');
    setFilterStatus(undefined);
    const clearParams = { ...query };
    delete clearParams.dateFrom;
    delete clearParams.dateTo;
    delete clearParams.driver;
    delete clearParams.status;
    clearParams.current = 1;
    handleFilter(clearParams, true);
    setFilterOpen(false);
  };

  const activeFilterCount = [query.dateFrom || query.dateTo, query.driver, query.status].filter(Boolean).length;

  const NON_DELETABLE_STATUSES = ['DISPATCHED'];

  const handleBulkDeleteRoutes = async () => {
    const deletableRoutes = routes.filter(
      (r) => selectedRowKeys.includes(r.id) && !NON_DELETABLE_STATUSES.includes(r.status?.toUpperCase()),
    );
    const skippedCount = selectedRowKeys.length - deletableRoutes.length;

    let deleted = 0;
    for (const route of deletableRoutes) {
      try {
        await dispatch(doDeleteRoute({ id: route.id })).unwrap();
        deleted++;
      } catch {
        // continue with others
      }
    }

    if (deleted > 0) {
      message.success(t('routesDeleted', { count: deleted }));
    }
    if (skippedCount > 0) {
      message.warning(t('dispatchedCannotDelete', { count: skippedCount }));
    }

    setSelectedRowKeys([]);
    dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
  };

  const handleDeleteRoute = async (record) => {
    try {
      const result = await dispatch(doDeleteRoute({ id: record.id })).unwrap();
      message.success(t('routeDeleted', { id: record.id }));
      // Refresh the routes list
      dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDelete'));
    }
  };

  const withAction = (record) => {
    const isDeletable = !NON_DELETABLE_STATUSES.includes(record.status?.toUpperCase());

    return (
      <div className="actions">
        <Link to={`/admin/route-plans?date=${moment(record.date).format(DATE_BACKEND_FORMAT)}`}>
          <Button type="link" className="edit">
            <ReactSVG className="icon" src="/icons/route-plans.svg" />
          </Button>
        </Link>
        {isDeletable ? (
          <Popconfirm
            title={t('deleteRoute')}
            description={t('deleteRouteConfirm', { id: record.id })}
            onConfirm={() => handleDeleteRoute(record)}
            okText={tc('delete')}
            cancelText={tc('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/more.svg" />
            </Button>
          </Popconfirm>
        ) : (
          <Button type="link" className="more" disabled>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="RoutesPage Content">
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
        <div className="HeaderToolBar"></div>
      </div>
      <div className="page-header-table">
        <div className="search">
          <Input
            onChange={handleChangeSearch}
            onBlur={handleSearch}
            placeholder={tc('search')}
            onKeyDown={handleKeyDown}
            suffix={<img src="/icons/search.svg" />}
            disabled={isFetching}
            value={searchValue}
          />
        </div>
        <div className="buttons">
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={t('deleteRoutesConfirm', { count: selectedRowKeys.length })}
              description={t('onlyNonDispatched')}
              onConfirm={handleBulkDeleteRoutes}
              okText={tc('delete')}
              cancelText={tc('cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button className="FilterButton ButtonFilter">
                {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
              </Button>
            </Popconfirm>
          )}

          <Badge count={activeFilterCount} size="small" offset={[-5, 0]}>
            <Button className="FilterButton ButtonFilter" onClick={() => setFilterOpen(true)}>
              {tc('filter')}
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </Badge>
          <Button type="text" onClick={() => tableConfigRef.current?.openDrawer?.()}>
            <ReactSVG className="icon" src="/icons/settings.svg" />
          </Button>
        </div>
      </div>

      <Drawer
        title={t('filterRoutes')}
        placement="right"
        onClose={() => setFilterOpen(false)}
        open={filterOpen}
        zIndex={1010}
        width={360}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleClearFilters}>{tc('clear')}</Button>
            <Button type="primary" onClick={handleApplyFilters}>
              {tc('apply')}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('dateRange')}</label>
            <RangePicker
              style={{ width: '100%' }}
              value={filterDateRange}
              onChange={(dates) => setFilterDateRange(dates)}
              format="YYYY-MM-DD"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('driver')}</label>
            <Input
              placeholder={t('driverName')}
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              allowClear
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('status')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('allStatuses')}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              allowClear
              options={ROUTE_STATUSES}
            />
          </div>
        </div>
      </Drawer>
      <div className="ContentMain">
        <RoutesTable
          dataSource={routes}
          pagination={pagination}
          rowKey="id"
          isFetching={isFetching}
          onSelectChange={onSelectChange}
          configRef={tableConfigRef}
          onPageSizeChange={(size) => {
            setConfigPageSize(size);
            handleFilter({ ...query, pageSize: size, current: 1 }, true);
          }}
          selectedRowKeys={selectedRowKeys}
          withAction={withAction}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default RoutesPage;
